import { Map } from 'immutable'
import { takeEvery, select, call, put, all } from 'redux-saga/effects'
import { isNil, prop } from 'ramda'
import {
  updateOutputNode,
  SET_OUTPUT_FIELD,
  createScalingQuery,
  removeScalingQuery,
  UPDATE_OUTPUT_QUERY,
} from 'actions/feature/outputs'
import {
  ADD_SINGLE_CONDITION,
  setConditionField,
} from 'actions/feature/conditions'
import {
  getCondition,
  mergedConditionSelector,
} from 'selectors/feature/conditions'
import {
  getFunctionOutput,
  getDisabledCanOutputBits,
  getOutputQuery,
  getOutputQueryType,
  outputPinsSelector,
  makeGetOutputQueryUri,
} from 'selectors/feature/outputs'
import { getFeatureModuleName } from 'selectors/feature/featureMetadata'
import { getMustResetCanBitNumber } from 'selectors/feature/logicNodes'
import { getSignal } from 'selectors/feature/inputs'
import {
  AH,
  AMPS2,
  AMPS20,
  IGNITION,
  AUTOMATIC,
  MIN_FREQUENCY,
  MIN_DUTY_CYCLE,
  CHECPLUS_MODULE_TYPES,
  SIMPLE,
} from 'utils/constants'
import {
  isNativeOutput,
  isCanOutput,
  isTimer,
  isNativeInput,
  isPinOutput,
  isSimpleQuery,
} from 'utils/types/features'

import {
  DEFAULT_RESET_DELAY,
  DEFAULT_RESET_ATTEMPTS,
} from 'constants/featureOutputs'
import { OPERATORS, EVAL_VALUES } from 'components/utils'
import { calculateNewStartBit } from 'components/feature/utils/bitGrid'
import { canOutputId } from 'components/feature/utils/outputs'
import { makeLogicNode } from 'utils/feature'
import {
  ADD_LOGIC_NODE,
  ADD_LOGIC_NODE_TO_OUTPUT,
} from 'actions/feature/logicNodes'
import { SWITCH_INPUT_TYPE } from 'actions/feature/inputs'

export default function* watchOutputField() {
  yield takeEvery(SET_OUTPUT_FIELD, handleOutputFieldChange)
}

const handlers = {
  current: updateCurrentSideEffects,
  resetType: updateResetAttempts,
  polarity: updatePolaritySideEffects,
  numberOfBits: updateBits,
  type: updateOutputNodeOnTypeChange,
  pwmMode: addOrRemoveQuery,
  mappedOutputId: updateIsOutputBoolean,
}

function* handleOutputFieldChange(action) {
  const { paramName, value, id } = action

  const nodeSubset = Map({
    [paramName]: value,
    ...(paramName === 'floatingCircuit' && { mappedOutputId: null }),
    ...(paramName === 'current' && { mappedOutputId: null }),
  })

  if (handlers[paramName]) {
    const output = yield select(getFunctionOutput, { id })

    const expandedSubset = yield call(handlers[paramName], {
      value,
      id,
      output,
      nodeSubset,
    })

    yield put(updateOutputNode(id, expandedSubset))
  } else {
    yield put(updateOutputNode(id, nodeSubset))
  }
}

function* updateIsOutputBoolean({ value, nodeSubset, output }) {
  const outputQueryUri = yield select(makeGetOutputQueryUri(), {
    fnId: output.fnId,
  })

  const outputQuery = yield select(getCondition, { queryUri: outputQueryUri })

  // if pin output with selected single condition value
  const isPinOutputType = isPinOutput(prop('type', output))
  const isSingleConditionWithSignal =
    isSimpleQuery(prop('queryType', outputQuery)) && prop('signal', outputQuery)

  // from bool to non bool
  // scrub signal, input config, floating circuits
  const moduleSignals = yield select(outputPinsSelector)
  const bool = moduleSignals.getIn([value, 'isOutputBoolean'])
  if (output.isOutputBoolean !== bool) {
    if (outputQueryUri) {
      yield put(setConditionField(outputQueryUri, 'numberOfBits', bool ? 1 : 4))

      if (!isPinOutputType && !isSingleConditionWithSignal) {
        // from bool to non bool
        // scrub signal, input config, floating circuits
        yield put(setConditionField(outputQueryUri, 'signal', null))
        yield put(setConditionField(outputQueryUri, 'inputConfig', null))
        yield put(setConditionField(outputQueryUri, 'floatingCircuit', null))
      }
    }
  }
  return nodeSubset.set('isOutputBoolean', bool).set('floatingCircuit', null)
}

function* updateCurrentSideEffects({ value, output, nodeSubset }) {
  const { polarity, resetType } = output

  if (value !== AMPS2 && polarity !== AH) {
    return yield updatePolaritySideEffects({
      value: AH,
      output,
      nodeSubset: nodeSubset.set('polarity', AH),
    })
  }

  if (value === AMPS20 && isNil(resetType)) {
    return yield updateResetAttempts({
      value: IGNITION,
      output,
      nodeSubset: nodeSubset
        .set('virtualFuseSetting', 20)
        .set('resetType', IGNITION),
    })
  }
  return nodeSubset
}

function updateResetAttempts({ value, output, nodeSubset }) {
  const { resetAttempts } = output

  if (isNil(resetAttempts) && value === AUTOMATIC) {
    return nodeSubset
      .set('resetAttempts', DEFAULT_RESET_ATTEMPTS)
      .set('resetDelay', DEFAULT_RESET_DELAY)
  }
  return nodeSubset
}

function* updatePolaritySideEffects({ value, output, nodeSubset }) {
  const { frequency, dutyCycle, pwmMode } = output

  const pwmShouldBeOne = value === 'PWM' && isNil(pwmMode)
  const pwmShouldBeNull = value !== 'PWM' && !isNil(pwmMode) && pwmMode !== 1

  const modifiedFields = Map({
    ...(isNil(frequency) && { frequency: MIN_FREQUENCY }),
    ...(isNil(dutyCycle) && { dutyCycle: MIN_DUTY_CYCLE }),
    ...(pwmShouldBeOne && { pwmMode: 1 }),
    ...(pwmShouldBeNull && { pwmMode: null }),
    mappedOutputId: null,
  })
  return yield addOrRemoveQuery({
    value: modifiedFields.get('pwmMode'),
    output,
    nodeSubset: nodeSubset.merge(modifiedFields),
  })
}

function* updateBits({ value: numberOfBits, id, output, nodeSubset }) {
  const { startBit, fnId } = output
  const disabledBits = yield select(getDisabledCanOutputBits(id))

  // if changing to sixteenBitCan input and single condition can input is length
  // 1 or 2, then change to 4 for first non-disabled option
  if (numberOfBits === 16) {
    const outputQuery = yield select(getOutputQuery, { fnId })

    if (outputQuery) {
      const { numberOfBits: inputNumberOfBits, uri } = outputQuery

      const invalidInputBits =
        inputNumberOfBits === 1 || inputNumberOfBits === 2

      if (invalidInputBits) {
        yield put(setConditionField(uri, 'numberOfBits', 4))
      }
    }
  }

  const newStartBit = calculateNewStartBit(
    startBit,
    numberOfBits,
    'output',
    disabledBits
  )

  const featureModuleName = yield select(getFeatureModuleName)
  const newOutputId = canOutputId(featureModuleName, numberOfBits)
  return yield updateIsOutputBoolean({
    value: newOutputId,
    output,
    nodeSubset: nodeSubset
      .set('startBit', newStartBit)
      .set('mappedOutputId', newOutputId),
  })
}

function* updateOutputNodeOnTypeChange({ value, output, nodeSubset }) {
  const { numberOfBits, fnId } = output
  const outputQueryType = yield select(getOutputQueryType, { fnId })
  const outputQuery = yield select(getOutputQuery, { fnId })

  const { subType, signal, uri } = outputQuery
  const featureModuleName = yield select(getFeatureModuleName)
  const isASAM = featureModuleName === CHECPLUS_MODULE_TYPES.ASAM

  const outputQueryIsTimer = isTimer(outputQueryType)

  const initialUpdatedOutputNode = nodeSubset.merge(
    Map(
      outputQueryIsTimer
        ? {
            value: 0,
            operator: OPERATORS.EQUAL,
          }
        : {
            value: null,
            operator: null,
            current: 5,
            polarity: AH,
          }
    )
  )

  const updatedOutputNode = !isASAM
    ? yield updatePolaritySideEffects({
        value: null,
        output,
        nodeSubset: initialUpdatedOutputNode.merge(
          Map({
            powerNetLevel: null,
            isAwake: null,
            current: 5,
            polarity: AH,
          })
        ),
      })
    : initialUpdatedOutputNode

  if (isNativeOutput(value)) {
    if (isNativeInput(subType)) {
      // if we change type to native output, and a native input signal that is NOT boolean
      // is set, then we need to nullify the signal in the query (only boolean signals are valid)
      const { translationType } = yield select(getSignal, { signal })

      if (translationType !== 'boolean') {
        yield put(setConditionField(uri, 'signal', null))
      }
    }
    const nativeOutputNodeProps = Map({
      ...(!outputQueryIsTimer && {
        value: EVAL_VALUES.TRUE,
        operator: OPERATORS.EQUAL,
      }),
      floatingCircuit: null,
    })

    return updatedOutputNode.merge(nativeOutputNodeProps)
  }

  if (isCanOutput(value)) {
    const mustResetCanBitNumber = yield select(getMustResetCanBitNumber, {
      fnId,
    })
    const mustResetXMCCanBitNumber = mustResetCanBitNumber && !isASAM
    const defaultNumberOfBits = isASAM ? 16 : 2
    const canOutputNodeProps = Map({
      floatingCircuit: null,
      mappedOutputId: canOutputId(
        featureModuleName,
        numberOfBits || defaultNumberOfBits
      ),
      ...(mustResetXMCCanBitNumber && { numberOfBits: 2 }),
      ...(!mustResetXMCCanBitNumber &&
        !numberOfBits && {
          numberOfBits: defaultNumberOfBits,
          value: null,
          operator: null,
        }),
    })
    return yield updateIsOutputBoolean({
      value,
      output,
      nodeSubset: updatedOutputNode.merge(canOutputNodeProps),
    })
  }

  // PIN output: restore evaluation defaults so the evaluation UI is visible
  // and the output evaluates to True (matching initial output creation defaults).
  // The initial reset above clears value/operator to null; without restoring them
  // getShowOutputEvaluation returns false and the saved value defaults to False.
  if (!outputQueryIsTimer) {
    return updatedOutputNode.merge(
      Map({
        value: EVAL_VALUES.TRUE,
        operator: OPERATORS.EQUAL,
      })
    )
  }

  return updatedOutputNode
}

function* addOrRemoveQuery({ value, output, nodeSubset }) {
  const { id, scalingQuery } = output

  if (!scalingQuery && value && value !== 1) {
    yield put(createScalingQuery(id))
  }

  if (scalingQuery && (value === 1 || !value)) {
    yield put(removeScalingQuery(id))
  }
  return nodeSubset
}

export function* watchAddLogicNodeToOutput() {
  yield takeEvery(ADD_LOGIC_NODE_TO_OUTPUT, handleAddLogicNodeToOutput)
}

function* handleAddLogicNodeToOutput({
  nodeType,
  nextIndex,
  fnId,
  displayLogicNodeAsGhost,
  performSyncronousOutputUpdate,
}) {
  if (nodeType === SIMPLE) {
    if (!performSyncronousOutputUpdate) {
      yield all([
        put({
          type: SWITCH_INPUT_TYPE,
          subType: 'nativeInput',
          options: { defaultNumberOfBits: null },
          queryType: SIMPLE,
        }),
        put({
          type: ADD_SINGLE_CONDITION,
          subType: 'nativeInput',
          fnId,
          isSixteenBitCan: false,
          isNativeOutput: false,
        }),
        put({
          type: ADD_LOGIC_NODE_TO_OUTPUT,
          performSyncronousOutputUpdate: true,
          fnId,
          nodeType,
          displayLogicNodeAsGhost,
        }),
      ])
    } else {
      const outputQuery = yield select(getOutputQuery, { fnId })
      const condition = yield select(mergedConditionSelector, {
        queryUri: prop('uri', outputQuery),
      })
      const singleConditionUri = prop('uri', condition.toJS())
      yield put({
        type: UPDATE_OUTPUT_QUERY,
        queryUri: singleConditionUri,
        fnId,
      })
    }
  } else {
    const { logicNode, conditions } = makeLogicNode(nodeType, nextIndex)

    const { uri } = logicNode

    yield all([
      put({
        type: ADD_LOGIC_NODE,
        nodeType,
        nextIndex,
        logicDetails: {
          logicNode,
          conditions,
        },
      }),
      put({
        type: UPDATE_OUTPUT_QUERY,
        queryUri: uri,
        fnId,
      }),
    ])
  }
}
